import { useEffect, useMemo, useRef, useState } from "react";
import { AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, Badge, Box, Button, Divider, Flex, IconButton, Input, InputGroup, InputLeftElement, List, ListItem, Spinner, Text, VStack } from "@chakra-ui/react";
import { WarningIcon, SearchIcon, CloseIcon } from "@chakra-ui/icons";
import { CustomToast } from "../../../../components";
import { useFetch } from "../../../../hooks/useFetch";

interface Candidate { employee_Id: number; employee_Name: string; employee_Code: string; }

interface BranchGroup {
    branchId: number;
    branchName: string;
    memberIds: number[];
}

const BranchReassignCard = ({ group, excludeEmployeeId, selected, onSelect, }: {
    group: BranchGroup;
    excludeEmployeeId: number;
    selected: Candidate | null;
    onSelect: (branchId: number, candidate: Candidate | null) => void;
}) => {
    const { addToast } = CustomToast();
    const { fetchApi } = useFetch(addToast);

    const [query, setQuery] = useState('');
    const [debounced, setDebounced] = useState('');
    const [results, setResults] = useState<Candidate[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(query), 400);
        return () => clearTimeout(t);
    }, [query]);

    useEffect(() => {
        if (!debounced.trim()) { setResults([]); return; }
        (async () => {
            setLoading(true);
            const params = new URLSearchParams();
            params.append('filter', debounced);
            params.append('excludeEmployeeId', String(excludeEmployeeId));
            params.append('branchIds', String(group.branchId));

            const res = await fetchApi<Candidate[]>('TeamInteraction/teamLeadManagerSearch', 'GET', '', params.toString());
            setResults(res || []);
            setLoading(false);
        })();
    }, [debounced, group.branchId, excludeEmployeeId]);

    const noResults = !!debounced.trim() && !loading && results.length === 0;

    return (
        <Box border="1px solid #E2E8F0" borderRadius="10px" p={3}>
            <Flex justify="space-between" align="center" mb={2}>
                <Text fontSize="13px" fontWeight="700" color="#0F172A">{group.branchName}</Text>
                <Badge bg="#F0FDF4" color="#15803D" borderRadius="full" fontSize="11px" px={2}>
                    {group.memberIds.length} member(s)
                </Badge>
            </Flex>

            {selected ? (
                <Flex align="center" gap={2} bg="#F0FDF4" border="1px solid #BBF7D0" borderRadius="8px" px={3} py={2}>
                    <Box w="7px" h="7px" borderRadius="full" bg="#16A34A" />
                    <Text fontSize="13px" fontWeight="600" color="#15803D" flex={1}>{selected.employee_Name}</Text>
                    <IconButton aria-label="Clear" icon={<CloseIcon boxSize={2} />} size="xs" variant="ghost" onClick={() => onSelect(group.branchId, null)} />
                </Flex>
            ) : (
                <Box position="relative">
                    <InputGroup size="sm">
                        <InputLeftElement pointerEvents="none" h="34px"><SearchIcon color="gray.400" boxSize={3} /></InputLeftElement>
                        <Input h="34px" pl={8} fontSize="13px" placeholder={`Search TL / Manager for ${group.branchName}…`} value={query} onChange={e => setQuery(e.target.value)} />
                        {loading && <Spinner size="xs" position="absolute" right={3} top="9px" />}
                    </InputGroup>

                    {noResults && (
                        <Text fontSize="11px" color="#DC2626" mt={1.5}>
                            No one has access to {group.branchName}.
                        </Text>
                    )}

                    {results.length > 0 && (
                        <Box position="absolute" top="100%" left={0} right={0} zIndex={20} bg="white" border="1px solid #E2E8F0" borderRadius="8px" boxShadow="0 4px 16px rgba(0,0,0,0.10)" maxH="180px" overflowY="auto" mt={1}>
                            {results.map(c => (
                                <Flex key={c.employee_Id} align="center" gap={2} px={3} py={2} cursor="pointer" _hover={{ bg: '#F0FDF4' }} onClick={() => { onSelect(group.branchId, c); setQuery(''); setResults([]); }}>
                                    <Text fontSize="13px" fontWeight="500">{c.employee_Name}</Text>
                                    <Text fontSize="11px" color="#94A3B8" fontFamily="monospace">{c.employee_Code}</Text>
                                </Flex>
                            ))}
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

const RemoveRoleDialog = ({ isOpen, onClose, leader, onRemoved }: any) => {
    const { addToast } = CustomToast();
    const { fetchApi } = useFetch(addToast);
    const [saving, setSaving] = useState(false);
    const cancelRef = useRef<HTMLButtonElement>(null);
    const confirmCancelRef = useRef<HTMLButtonElement>(null);

    const [selections, setSelections] = useState<Record<number, Candidate | null>>({});
    const [confirmAction, setConfirmAction] = useState<'skip' | 'reassign' | null>(null);

    const excludeEmployeeId = leader?.employeeId ?? leader?.id;
    const teamMembers = leader?.teamMembers || [];
    const hasMembers = teamMembers.length > 0;

    const branchGroups: BranchGroup[] = useMemo(() => {
        const map = new Map<number, BranchGroup>();
        teamMembers.forEach((m: any) => {
            const branchId = m.branch_Id;
            if (branchId === null || branchId === undefined) return;
            if (!map.has(branchId)) {
                const label = m.company ? `${m.company} - ${m.branch}` : (m.branch || `Branch ${branchId}`);
                map.set(branchId, { branchId, branchName: label, memberIds: [] });
            }
            map.get(branchId)!.memberIds.push(m.employeeId ?? m.id);
        });
        return Array.from(map.values());
    }, [teamMembers]);

    useEffect(() => {
        if (!isOpen) {
            setSelections({});
            setConfirmAction(null);
        }
    }, [isOpen]);

    const handleSelect = (branchId: number, candidate: Candidate | null) => {
        setSelections(prev => ({ ...prev, [branchId]: candidate }));
    };

    const allAssigned = branchGroups.length > 0 && branchGroups.every(g => !!selections[g.branchId]);

    const runSkip = async () => {
        setSaving(true);
        try {
            const res = await fetchApi('TeamInteraction/removeRole', 'POST', {
                employee_Id: excludeEmployeeId,
                role_Type: leader?.role,
                new_Leader_Id: null,
            });
            if (res) {
                addToast({ message: 'Role removed successfully', status: 'success' });
                onRemoved?.();
            }
        } finally {
            setSaving(false);
            setConfirmAction(null);
        }
    };

    const runReassign = async () => {
        setSaving(true);
        try {
            const moveResults = await Promise.all(
                branchGroups.map(g =>
                    fetchApi('TeamInteraction/moveMembers', 'POST', {
                        member_Employee_Ids: g.memberIds,
                        new_Leader_Id: selections[g.branchId]!.employee_Id,
                        role_Type: leader?.role,
                    })
                )
            );
            if (!moveResults.every(r => r)) return;

            // Members are already reassigned above, so this just removes the role itself.
            const res = await fetchApi('TeamInteraction/removeRole', 'POST', {
                employee_Id: excludeEmployeeId,
                role_Type: leader?.role,
                new_Leader_Id: null,
            });
            if (res) {
                addToast({ message: 'Role removed and team reassigned successfully', status: 'success' });
                onRemoved?.();
            }
        } finally {
            setSaving(false);
            setConfirmAction(null);
        }
    };

    const multiBranch = branchGroups.length > 1;

    return (
        <>
            <AlertDialog isOpen={isOpen && !confirmAction} leastDestructiveRef={cancelRef} onClose={onClose} isCentered size={hasMembers ? 'lg' : 'md'}>
                <AlertDialogOverlay>
                    <AlertDialogContent borderRadius="12px">
                        <AlertDialogHeader fontSize="15px" fontWeight="700" display="flex" alignItems="center" gap={2}>
                            <WarningIcon color="#DC2626" boxSize={4} />
                            Remove {leader?.role} role
                        </AlertDialogHeader>
                        <AlertDialogBody fontSize="13px" color="#374151">
                            <Text>
                                This will remove {leader?.name} as {leader?.role} entirely.
                                {hasMembers
                                    ? ` ${teamMembers.length} member(s) currently reporting to them need a new ${leader?.role}, or can be left unassigned.`
                                    : ' They have no members currently reporting to them.'}
                            </Text>

                            {hasMembers && (
                                <>
                                    <Divider my={3} />
                                    <Text fontSize="12px" fontWeight="600" color="#374151" mb={2}>
                                        Reassign to (optional)
                                    </Text>

                                    {multiBranch && (
                                        <Text fontSize="11px" color="#B45309" bg="#FFFBEB" border="1px solid #FDE68A" borderRadius="6px" px={2} py={1.5} mb={2}>
                                            This team spans {branchGroups.length} branches. Pick a new {leader?.role} for each.
                                        </Text>
                                    )}

                                    <VStack spacing={3} align="stretch" maxH="320px" overflowY="auto">
                                        {branchGroups.map(g => (
                                            <BranchReassignCard
                                                key={g.branchId}
                                                group={g}
                                                excludeEmployeeId={excludeEmployeeId}
                                                selected={selections[g.branchId] ?? null}
                                                onSelect={handleSelect}
                                            />
                                        ))}
                                    </VStack>
                                </>
                            )}

                            <Text mt={3} fontSize="12px" color="#94A3B8">This cannot be undone from here.</Text>
                        </AlertDialogBody>
                        <AlertDialogFooter gap={3}>
                            <Button ref={cancelRef} variant="ghost" onClick={onClose} isDisabled={saving}>Cancel</Button>

                            {hasMembers && (
                                <Button variant="outline" colorScheme="gray" onClick={() => setConfirmAction('skip')}>
                                    Skip for now
                                </Button>
                            )}

                            <Button bg="#DC2626" color="white" _hover={{ bg: '#B91C1C' }}
                                isDisabled={hasMembers && !allAssigned}
                                onClick={() => hasMembers ? setConfirmAction('reassign') : runSkip()}>
                                {hasMembers ? 'Reassign & remove' : 'Yes, remove role'}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            {/* Second-step confirmation before anything actually runs */}
            <AlertDialog isOpen={!!confirmAction} leastDestructiveRef={confirmCancelRef} onClose={() => setConfirmAction(null)} isCentered>
                <AlertDialogOverlay>
                    <AlertDialogContent borderRadius="12px">
                        <AlertDialogHeader fontSize="15px" fontWeight="700" display="flex" alignItems="center" gap={2}>
                            <WarningIcon color="#DC2626" boxSize={4} />
                            {confirmAction === 'skip' ? 'Leave members unassigned?' : 'Confirm reassignment'}
                        </AlertDialogHeader>
                        <AlertDialogBody fontSize="13px" color="#374151">
                            {confirmAction === 'skip' ? (
                                <Text>
                                    {leader?.name} will be removed as {leader?.role}. All {teamMembers.length} member(s)
                                    across {branchGroups.length} branch(es) will be left with <b>no {leader?.role}</b> until reassigned separately.
                                </Text>
                            ) : (
                                <>
                                    <Text mb={2}>{leader?.name} will be removed as {leader?.role}, and members reassigned as follows:</Text>
                                    <List spacing={1} fontSize="12px" color="#374151">
                                        {branchGroups.map(g => (
                                            <ListItem key={g.branchId}>
                                                • {g.memberIds.length} member(s) in <b>{g.branchName}</b> → {selections[g.branchId]?.employee_Name}
                                            </ListItem>
                                        ))}
                                    </List>
                                </>
                            )}
                            <Text mt={2} fontSize="12px" color="#94A3B8">This cannot be undone from here.</Text>
                        </AlertDialogBody>
                        <AlertDialogFooter gap={3}>
                            <Button ref={confirmCancelRef} variant="ghost" onClick={() => setConfirmAction(null)} isDisabled={saving}>Back</Button>
                            <Button bg="#DC2626" color="white" _hover={{ bg: '#B91C1C' }} isLoading={saving} loadingText="Processing…"
                                onClick={() => confirmAction === 'skip' ? runSkip() : runReassign()}>
                                Confirm
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    );
};

export default RemoveRoleDialog;